import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { QUARTILES_QUERY } from "./query";
/**
 * Typed handler for the Overview dashboard Video Quartiles widget. Owns the
 * LookerML query construction, response typing, and caching so the frontend
 * hook never builds a raw query envelope (see the OA-131 review feedback that
 * required all overview fetches to go through typed SDK handlers).
 */
export default class LookerOverviewQuartiles extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-quartiles");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const queryBody = {
            ...QUARTILES_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter)
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        if (mediaTypeFilter?.value) {
            queryBody.filters["explore_overview.product_group"] = mediaTypeFilter.value;
        }
        // Fail closed: without advertiser/campaign ids the query would aggregate
        // quartiles across every partner. This only rejects missing ids — it does
        // not verify the caller owns the ids supplied, which is an existing gap
        // shared with the advertiser/campaign filter lookups this depends on.
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (!advertiserIds?.length) {
            throw new Error("Advertiser ids filter is required");
        }
        queryBody.filters["advertisers.advertiser_id"] = advertiserIds.join(",");
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (!campaignIds?.length) {
            throw new Error("Campaign ids filter is required");
        }
        queryBody.filters["campaigns.campaign_id"] = campaignIds.join(",");
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatResponse(data),
                queryMeta
            }
        ])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch overview dashboard video quartiles");
        }));
    }
    formatResponse(data) {
        const row = data[0];
        return [
            {
                video: {
                    impressions: row?.["explore_overview.video_only_impressions"] || 0,
                    starts: row?.["explore_overview.video_only_starts"] || 0,
                    q1: row?.["explore_overview.video_only_q1"] || 0,
                    q2: row?.["explore_overview.video_only_q2"] || 0,
                    q3: row?.["explore_overview.video_only_q3"] || 0,
                    completes: row?.["explore_overview.video_only_completes"] || 0
                },
                audio: {
                    impressions: row?.["explore_overview.audio_only_impressions"] || 0,
                    starts: row?.["explore_overview.audio_only_starts"] || 0,
                    q1: row?.["explore_overview.audio_only_q1"] || 0,
                    q2: row?.["explore_overview.audio_only_q2"] || 0,
                    q3: row?.["explore_overview.audio_only_q3"] || 0,
                    completes: row?.["explore_overview.audio_only_completes"] || 0
                }
            }
        ];
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Quartiles.js.map